import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Robot, ArrowUpRight } from "@phosphor-icons/react";
import { useAITasks } from "../lib/aiTasks";

/**
 * Floating "AI is bezig"-pillbadge, permanently anchored to the
 * bottom-right of the viewport as long as at least one AI task is
 * registered via `AITaskProvider`. Shows the task label, elapsed
 * seconds, and (optionally) a link back to the originating page.
 *
 * If multiple tasks run in parallel we stack them upward — this is
 * expected to be very rare in practice but the layout handles it.
 */
export default function AITaskPill() {
  const { tasks } = useAITasks();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (tasks.length === 0) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [tasks.length]);

  if (tasks.length === 0) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-40 flex flex-col-reverse gap-2 pointer-events-none"
      data-testid="ai-task-pill-container"
    >
      {tasks.map((t) => (
        <AITaskPillCard key={t.id} task={t} now={now} />
      ))}
    </div>
  );
}

function AITaskPillCard({ task, now }) {
  const elapsed = Math.max(0, Math.floor((now - task.startedAt) / 1000));
  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;
  const timeLabel =
    minutes > 0
      ? `${minutes}m ${seconds.toString().padStart(2, "0")}s`
      : `${seconds}s`;
  return (
    <div
      className="pointer-events-auto flex items-center gap-3 bg-zinc-900 text-white px-4 py-2.5 rounded-full shadow-lg border border-zinc-700 min-w-[240px] max-w-sm"
      data-testid={`ai-task-pill-${task.id}`}
      role="status"
      aria-live="polite"
    >
      <div className="shrink-0 relative">
        <Robot size={20} weight="duotone" className="text-white" />
        <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 leading-none">AI is bezig</div>
        <div className="text-sm font-semibold truncate leading-tight mt-0.5" title={task.label}>
          {task.label}
        </div>
      </div>
      <div className="shrink-0 text-xs tabular-nums text-zinc-300 font-mono" data-testid={`ai-task-pill-elapsed-${task.id}`}>
        {timeLabel}
      </div>
      {task.returnTo && (
        <Link
          to={task.returnTo}
          className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-white/90 hover:text-white border-l border-zinc-700 pl-3 ml-1"
          data-testid={`ai-task-pill-goto-${task.id}`}
        >
          Ga terug <ArrowUpRight size={12} weight="bold" />
        </Link>
      )}
    </div>
  );
}
